import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MapPin,
  Heart,
  Bookmark,
  Store,
  Settings,
  Lock,
  LifeBuoy,
  LogOut,
  ChevronRight,
  X,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useMyInterests, useSavedPosts } from '@/hooks/useFeed'
import { useMyFollows } from '@/hooks/useBusiness'
import { STRIPE } from '@/data/mockData'
import FollowedBusinessesList from '@/components/features/business/FollowedBusinessesList'

const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  error: '#C85C5C',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, profile, signOut } = useAuth()
  const myInterests = useMyInterests()
  const { data: savedData } = useSavedPosts()
  const savedCount = savedData?.data.length ?? 0
  const myFollows = useMyFollows()
  const [showFollowing, setShowFollowing] = useState(false)

  const stat = (n: number | string, label: string, onClick: () => void) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '16px 20px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, color: C.ink }}>{n}</div>
      <div style={{ fontSize: 12, color: C.ink2, marginTop: 2 }}>{label}</div>
    </button>
  )

  const menuItem = (
    icon: React.ReactNode,
    label: string,
    value?: string | number,
    onClick?: () => void,
    danger?: boolean
  ) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 20px',
        borderBottom: `1px solid ${C.border}`,
        cursor: 'pointer',
        color: danger ? C.error : C.ink,
      }}
      onClick={onClick ?? (() => {})}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: C.grayBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 14.5 }}>{label}</span>
      {value !== undefined && <span style={{ fontSize: 13.5, color: C.ink2 }}>{value}</span>}
      <ChevronRight size={16} color={C.ink2} />
    </div>
  )

  return (
    <div className="page-sm">
      <div className="three-col">
        {/* Tarjeta de perfil */}
        <div style={{ position: 'sticky', top: 20, minWidth: 0 }}>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              overflow: 'hidden',
              marginBottom: 14,
            }}
          >
            <div style={{ height: 90, background: STRIPE(C.teal, '#17706f') }} />
            <div style={{ padding: '0 20px 20px', marginTop: -36 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 99,
                  background: STRIPE(C.teal, '#17706f'),
                  border: '4px solid #fff',
                  marginBottom: 12,
                }}
              />
              <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 20, marginBottom: 2 }}>
                {profile?.username ?? user?.email?.split('@')[0] ?? 'usuario'}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.ink2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <MapPin size={13} /> Pereira, Risaralda
              </div>
            </div>
            <div style={{ display: 'flex', borderTop: `1px solid ${C.border}` }}>
              {stat(savedCount, 'Guardados', () =>
                navigate('/saved', { state: { from: 'profile' } })
              )}
              {stat(myInterests.size, 'Intereses', () => navigate('/interests'))}
              {stat(myFollows.size, 'Siguiendo', () => setShowFollowing(true))}
            </div>
          </div>

          <button
            onClick={signOut}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: '#fff',
              color: C.error,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <LogOut size={17} /> Cerrar sesión
          </button>
        </div>

        {/* Menú */}
        <div
          style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 18,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${C.border}`,
              fontFamily: FH,
              fontWeight: 700,
              fontSize: 13,
              color: C.ink2,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Mi actividad
          </div>
          {menuItem(
            <Heart size={18} color={C.ink2} />,
            'Actividades de interés',
            myInterests.size,
            () => navigate('/interests')
          )}
          {menuItem(<Bookmark size={18} color={C.ink2} />, 'Guardados', savedCount, () =>
            navigate('/saved', { state: { from: 'profile' } })
          )}
          {menuItem(<Store size={18} color={C.ink2} />, 'Negocios que sigo', myFollows.size, () =>
            navigate('/following')
          )}

          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${C.border}`,
              fontFamily: FH,
              fontWeight: 700,
              fontSize: 13,
              color: C.ink2,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            Cuenta
          </div>
          {menuItem(<Settings size={18} color={C.ink2} />, 'Configuración', undefined, () =>
            navigate('/settings')
          )}
          {menuItem(<Lock size={18} color={C.ink2} />, 'Privacidad y datos', undefined, () =>
            navigate('/privacy')
          )}
          {menuItem(<LifeBuoy size={18} color={C.ink2} />, 'Centro de ayuda', undefined, () =>
            navigate('/help')
          )}

          <div style={{ padding: '16px 20px' }}>
            <button
              onClick={signOut}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: C.error,
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>

      {showFollowing && (
        <>
          <div
            onClick={() => setShowFollowing(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99,
              background: 'rgba(0,0,0,.5)',
              backdropFilter: 'blur(2px)',
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: 100,
              width: 'min(480px, calc(100vw - 32px))',
              maxHeight: 'min(600px, calc(100vh - 64px))',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,.25)',
              animation: 'pxFadeUp .2s ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 20px',
                borderBottom: `1px solid ${C.border}`,
                flexShrink: 0,
              }}
            >
              <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 17 }}>
                Negocios que sigo
              </span>
              <button
                onClick={() => setShowFollowing(false)}
                aria-label="Cerrar"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  border: 'none',
                  background: C.grayBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: C.ink2,
                }}
              >
                <X size={17} />
              </button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto' }}>
              <FollowedBusinessesList onNavigate={() => setShowFollowing(false)} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
